<script setup lang="ts">
import { ref } from 'vue';
import {loadImage} from "@/shared/utils/image.ts";

// Define props with types
interface Props {
  src: string;
  alt?: string;
  fallbackSrc?: string;
  faIcon?: string;
  classes?: string;
  iconClasses?: string;
}

const props = defineProps<Props>();

const loadFaIcon = ref(false);

// Default values for alt text and fallback source
const altText = props.alt ?? 'Image';
const fallbackImageSrc = props.fallbackSrc || "/images/fallback.webp";

// Reactive state for image loading and source
const isImageLoaded = ref(false);
const imageSource = ref('');

// Load image source
imageSource.value = loadImage(props.src);

// Handle image load event
const handleImageLoad = () => {
  isImageLoaded.value = true;
};

// Handle image error event and set fallback source
const handleImageError = () => {
  if (props.faIcon) {
    loadFaIcon.value = true;
    isImageLoaded.value = true;
    return;
  }

  imageSource.value = loadImage(fallbackImageSrc);
  isImageLoaded.value = true;
};
</script>

<template>
  <div :class="[
    'relative flex items-center justify-around p-2' && (props.classes ?? '')
  ]">
    <img
      :src="imageSource"
      :alt="altText"
      @error="handleImageError"
      @load="handleImageLoad"
      :class="[
        'block w-full h-auto bg-none',
        { 'image-loaded': isImageLoaded },
      ]"
      v-if="!loadFaIcon"
    />
    <div
      v-else
      :class="[
        (props.iconClasses ?? 'p-3 bg-primary')
      ]"
    >
      <font-awesome-icon :icon="props.faIcon" class="w-full h-full text-black" />
    </div>
    <slot
      v-if="!isImageLoaded"
      name="loading"
      class="absolute bg-septenary text-secondary"
    >
      <p class="text-secondary">
        Loading...
      </p>
    </slot>
    <div  class="placeholder">
    </div>
  </div>
</template>

<style scoped lang="css">
.image-loaded {
  opacity: 1;
  transition: opacity 0.3s;
}
</style>
